/**
 * NEXUMA — ReportKit
 *
 * Primitivat e përbashkëta të faqeve të raporteve. Para këtij skedari çdo raport
 * e rindërtonte vetë të njëjtën "chrome" — shiritin e filtrave, kutitë e shifrave,
 * grupin e butonave të eksportit, gjendjet bosh/duke u ngarkuar — secili me
 * dendësinë dhe paletën e vet. Rezultati ishte 20+ faqe që s'ngjanin me njëra-
 * tjetrën dhe që e mbishkruanin me `sx` stilin e tabelave që tema tashmë e jep.
 *
 * Rregullat që zbaton ky kit:
 *
 *   1. Dendësi e vetme. `ReportPage` e ngushton tabelën brenda vetes (6/12px në
 *      vend të 12/16px të temës) — raportet lexohen si fletë të dendura, jo si
 *      lista të gjera. Ndryshoje këtu, jo faqe-më-faqe.
 *   2. Një theks, jo ylber. Ngjyra e markës mban theksin; e verdha/e kuqja
 *      mbeten për status. Kutitë e shifrave janë neutrale me përjashtim të asaj
 *      kryesore (`accent`) dhe atyre që mbajnë vërtet një gjendje (`tone`).
 *   3. Asnjë hex i hardkoduar. Gjithçka nga `theme.palette`, që faqja të mos
 *      thyhet kur ndryshon tema.
 *   4. Eksporti është një buton me menu, jo tre butona me tre ngjyra. Kursen
 *      gjerësi dhe e heq ylberin nga këndi i sipërm i çdo tabele.
 */

import React from 'react';
import {
  Box, Paper, Stack, Typography, Button, Menu, MenuItem, ListItemIcon,
  Chip, TableCell, TableRow, CircularProgress, Divider, Tooltip, IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import GridOnOutlinedIcon       from '@mui/icons-material/GridOnOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionOutlinedIcon  from '@mui/icons-material/DescriptionOutlined';
import PrintOutlinedIcon        from '@mui/icons-material/PrintOutlined';
import MailOutlineIcon          from '@mui/icons-material/MailOutline';
import RefreshIcon              from '@mui/icons-material/Refresh';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';

// ── Dendësia e raporteve ─────────────────────────────────────────────────────
// E mbajmë si objekt `sx` mbi kontejnerin: prek vetëm tabelat brenda një faqeje
// raporti, jo tabelat e pjesës tjetër të aplikacionit.
const DENSE_TABLE_SX = {
  // Kartat e filtrave: tema jep 20px padding, që në një faqe raporti hante një
  // rresht të tërë përpara se të fillonte tabela. 12px mjaftojnë.
  //
  // ⚠️ Njësitë shkruhen HAPUR. Te `sx`, `padding` është veti me hap teme: `12`
  // do të thotë 12 × 8px = **96px**, jo 12px. Pa `'px'`, çdo `CardContent`
  // brenda një raporti merrte 96px padding nga të katër anët — kutitë e shifrave
  // te "Raport i punëtorit" mbeteshin me 39px përmbajtje dhe teksti thyhej
  // shkronjë për shkronjë. Prekte 18 faqe raporti njëherësh, sepse rregulli është
  // përzgjedhës pasardhës dhe fiton mbi `sx`-in e vetë kartës.
  '& .MuiCardContent-root': { padding: '12px' },
  '& .MuiCardContent-root:last-child': { paddingBottom: '12px' },
  // Alert-at informues të raporteve janë një rresht teksti, jo një bllok.
  '& .MuiAlert-root': { paddingTop: '2px', paddingBottom: '2px' },
  '& .MuiAlert-message': { padding: '6px 0', fontSize: '0.8125rem' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    padding: '7px 12px',
    fontSize: '0.65rem',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    padding: '6px 12px',
    fontSize: '0.8125rem',
  },
  // Kolonat numerike: shifra me gjerësi fikse rreshtohen vertikalisht dhe
  // krahasohen me sy — pa e ndërruar fontin e tërë tabelës.
  '& .num': {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
};

/**
 * Kontejneri i një faqeje raporti. Zëvendëson `<Box>`-in ad-hoc që kishte çdo
 * raport me padding-e të ndryshme, dhe sjell dendësinë e përbashkët.
 */
export function ReportPage({ children, sx }) {
  return (
    <Box sx={{ minWidth: 0, ...DENSE_TABLE_SX, ...sx }}>
      {children}
    </Box>
  );
}

/**
 * Shiriti i filtrave. Fëmijët janë fushat vetë (TextField-at `select`/`date`),
 * që raporti i njeh më mirë se kiti; kiti jep vetëm kornizën, dendësinë dhe
 * veprimet e përbashkëta (pastro / rifresko).
 */
export function ReportFilters({ children, onReset, onRefresh, dirty = false, actions }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        mb: 1.25,
        borderRadius: 2,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        '& .MuiTextField-root': { minWidth: 150, flex: '0 1 190px' },
      }}
    >
      {children}
      <Box sx={{ flex: 1 }} />
      {actions}
      {onReset && dirty && (
        <Tooltip title="Pastro filtrat" arrow>
          <IconButton size="small" onClick={onReset} sx={{ color: 'text.secondary' }}>
            <FilterAltOffOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
      {onRefresh && (
        <Tooltip title="Rifresko" arrow>
          <IconButton size="small" onClick={onRefresh} sx={{ color: 'text.secondary' }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}

/**
 * Rreshti i shifrave kryesore. `items`: { label, value, accent?, tone? }
 * — `accent` e vesh me ngjyrën e markës (një për raport, ajo kryesorja),
 * — `tone` ('success' | 'warning' | 'error') vetëm kur shifra mbart status.
 */
export function StatTiles({ items = [], columns }) {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  const cols = columns || Math.min(shown.length, 6);
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        mb: 1.25,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: `repeat(${Math.min(cols, 3)}, minmax(0, 1fr))`,
          md: `repeat(${cols}, minmax(0, 1fr))`,
        },
      }}
    >
      {shown.map((it) => (
        <Paper
          key={it.label}
          variant="outlined"
          sx={{
            px: 1.25,
            py: 0.875,
            borderRadius: 2,
            minWidth: 0,
            ...(it.accent && {
              borderColor: 'primary.main',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
            }),
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: '0.625rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {it.label}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: '1rem',
              fontWeight: 800,
              lineHeight: 1.3,
              fontVariantNumeric: 'tabular-nums',
              color: it.tone ? `${it.tone}.main` : it.accent ? 'primary.main' : 'text.primary',
            }}
          >
            {it.value}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

/**
 * Karta e tabelës: titull + numërues majtas, veprime djathtas, tabela brenda me
 * skroll horizontal të vetin, dhe një fund opsional për totalet.
 */
export function ReportCard({ title, count, countLabel, actions, footer, children, maxHeight = 560 }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {(title || actions) && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
        >
          {title && (
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700 }}>
              {title}
            </Typography>
          )}
          {count !== undefined && (
            <Chip
              size="small"
              label={countLabel ? `${count} ${countLabel}` : count}
              sx={{ height: 20, fontSize: '0.6875rem' }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {actions}
        </Stack>
      )}
      <Box sx={{ overflowX: 'auto', maxHeight, overflowY: 'auto' }}>{children}</Box>
      {footer && (
        <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          {footer}
        </Box>
      )}
    </Paper>
  );
}

/** Totalet në fund të një ReportCard — çifte etiketë/vlerë, të ndara me vija. */
export function ReportTotals({ items = [] }) {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" divider={<Divider orientation="vertical" flexItem />}>
      {shown.map((it) => (
        <Typography key={it.label} variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {it.label}{' '}
          <Box component="span" sx={{ fontWeight: 800, color: it.tone ? `${it.tone}.main` : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
            {it.value}
          </Box>
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * Eksporti si një buton i vetëm me menu. Kalo vetëm handler-at që raporti i ka
 * — zërat pa handler nuk shfaqen.
 */
export function ExportMenu({ onCsv, onExcel, onPdf, onPrint, onEmail, label = 'Eksporto', disabled = false, extra = [] }) {
  const [anchor, setAnchor] = React.useState(null);
  const close = () => setAnchor(null);
  const run = (fn) => () => { close(); fn(); };

  const entries = [
    onCsv   && { key: 'csv',   label: 'CSV',   icon: <DescriptionOutlinedIcon fontSize="small" />,  fn: onCsv },
    onExcel && { key: 'excel', label: 'Excel', icon: <GridOnOutlinedIcon fontSize="small" />,       fn: onExcel },
    onPdf   && { key: 'pdf',   label: 'PDF',   icon: <PictureAsPdfOutlinedIcon fontSize="small" />, fn: onPdf },
    onPrint && { key: 'print', label: 'Printo', icon: <PrintOutlinedIcon fontSize="small" />,       fn: onPrint },
    onEmail && { key: 'email', label: 'Dërgo me email', icon: <MailOutlineIcon fontSize="small" />,  fn: onEmail },
    ...extra,
  ].filter(Boolean);

  if (!entries.length) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 600,
          py: 0.375,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'text.disabled', bgcolor: 'grey.50' },
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {entries.map((e) => (
          <MenuItem key={e.key} onClick={run(e.fn)} sx={{ fontSize: '0.8125rem' }}>
            <ListItemIcon sx={{ minWidth: 30, color: 'text.secondary' }}>{e.icon}</ListItemIcon>
            {e.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Rreshti "duke u ngarkuar" brenda një tabele. */
export function LoadingRow({ colSpan }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 4, border: 0 }}>
        <CircularProgress size={22} />
      </TableCell>
    </TableRow>
  );
}

/** Rreshti bosh brenda një tabele — një mesazh, jo një qelizë e zbrazët. */
export function EmptyRow({ colSpan, message = 'Nuk ka të dhëna për këto filtra.' }) {
  return (
    <TableRow hover={false}>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 4, border: 0, color: 'text.secondary', fontSize: '0.8125rem' }}>
        {message}
      </TableCell>
    </TableRow>
  );
}

export { DENSE_TABLE_SX };
